import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartLineUp,
  CheckCircle,
  DeviceMobile,
  DownloadSimple,
  Info,
  WarningCircle,
} from "@phosphor-icons/react";
import { useAuth } from "../../app/auth";
import { DataFreshness, MetricStrip } from "../../components/Primitives";
import { QueryErrorState } from "../../components/States";
import { formatDateTime } from "../../lib/format";
import { useApiQuery } from "../../lib/query";
import { useToast } from "../../components/Toast";
import type { Approval, OperationsReport, TrendRow } from "../../lib/types";

function saveReportCsv(contents: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `xmirror-operations-report-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function ReportsPage() {
  const { request, hasPermission } = useAuth();
  const notify = useToast();
  const [period, setPeriod] = useState<"7d" | "30d">("30d");
  const summaryQuery = useApiQuery<OperationsReport>(
    ["reports", "operations-summary", period],
    `/api/reports/operations-summary?from=${new Date(Date.now() - (period === "7d" ? 7 : 30) * 86_400_000).toISOString()}`,
  );
  const trendsQuery = useApiQuery<{ period: string; timezone: string; items: TrendRow[] }>(
    ["reports", "task-trends", period],
    `/api/reports/task-trends?period=${period}`,
  );
  const metrics = summaryQuery.data?.metrics;
  const exportMutation = useMutation({
    mutationFn: async () => {
      const approval = await request<Approval>("/api/reports/export-approvals", {
        method: "POST",
        body: JSON.stringify({ reason: `管理端导出最近 ${period === "7d" ? 7 : 30} 天运营报表` }),
      });
      if (!hasPermission("approvals:review")) return { approval };
      await request(`/api/approvals/${approval.id}/review`, {
        method: "POST",
        body: JSON.stringify({ decision: "approved", comment: "管理员即时审批报表导出" }),
      });
      const from = new Date(Date.now() - (period === "7d" ? 7 : 30) * 86_400_000).toISOString();
      const csv = await request<string>(`/api/reports/export.csv?approvalId=${approval.id}&from=${encodeURIComponent(from)}`);
      return { approval, csv };
    },
    onSuccess: ({ approval, csv }) => {
      if (!csv) {
        notify("报表导出申请已提交", `审批编号 #${approval.id}`);
        return;
      }
      saveReportCsv(csv);
      notify("运营报表已导出", "文件包含指标口径、时间窗口和身份水印");
    },
  });
  if (summaryQuery.isError || trendsQuery.isError) {
    return <QueryErrorState onRetry={() => { void summaryQuery.refetch(); void trendsQuery.refetch(); }} />;
  }
  return (
    <div className="module-page">
      <div className="report-period">
        <div><ChartLineUp size={21} /><span><strong>运营分析</strong><small>指标由后端限时聚合生成，不下载全量明细。</small></span></div>
        <div className="toolbar-left">
          <DataFreshness updatedAt={summaryQuery.data?.generatedAt} isFetching={summaryQuery.isFetching || trendsQuery.isFetching} onRefresh={() => { void summaryQuery.refetch(); void trendsQuery.refetch(); }} />
          <div className="segmented"><button className={period === "7d" ? "active" : ""} onClick={() => setPeriod("7d")}>最近 7 天</button><button className={period === "30d" ? "active" : ""} onClick={() => setPeriod("30d")}>最近 30 天</button></div>
          {hasPermission("reports:export") && (
            <button className="secondary-button compact" disabled={exportMutation.isPending} onClick={() => exportMutation.mutate()}>
              <DownloadSimple size={16} />{exportMutation.isPending ? "正在申请…" : "审批导出"}
            </button>
          )}
        </div>
      </div>
      {exportMutation.isError && <div className="inline-error">报表导出失败，请检查审批状态后重试。</div>}
      <MetricStrip metrics={[
        { label: "创建任务", value: metrics?.taskCount ?? "—", note: "窗口内创建总数" },
        { label: "任务成功率", value: metrics ? `${(metrics.successRate * 100).toFixed(1)}%` : "—", note: `${metrics?.successfulTasks || 0} 个成功`, tone: "metric-green" },
        { label: "失败 / 超时", value: metrics?.failedTasks ?? "—", note: "窗口内失败终态", tone: metrics?.failedTasks ? "metric-red" : undefined },
        { label: "设备在线率", value: metrics ? `${(metrics.onlineRate * 100).toFixed(1)}%` : "—", note: `${metrics?.onlineDevices || 0} / ${metrics?.deviceCount || 0} 台在线`, tone: "metric-blue" },
      ]} />
      <section className="ops-kpi-grid" aria-label="运营待办指标">
        <article className={metrics?.pendingApprovals ? "attention" : ""}><span>待审批任务</span><strong>{metrics?.pendingApprovals ?? "—"}</strong><small>需要管理员决策</small></article>
        <article className={metrics?.activeAlerts ? "danger" : ""}><span>活动告警</span><strong>{metrics?.activeAlerts ?? "—"}</strong><small>尚未完成处置</small></article>
        <article className={metrics?.expiringCredentials ? "danger" : ""}><span>凭证需处理</span><strong>{metrics?.expiringCredentials ?? "—"}</strong><small>已过期或 7 天内到期</small></article>
        <article><span>平均执行时长</span><strong>{metrics ? `${(metrics.averageExecutionMs / 1000).toFixed(1)}s` : "—"}</strong><small>设备级已完成结果</small></article>
      </section>
      <section className="report-chart-card">
        <header><div><h2>任务执行趋势</h2><p>每日创建、成功和失败任务数</p></div><span>UTC · 更新于 {formatDateTime(summaryQuery.data?.generatedAt)}</span></header>
        <div className="chart-wrap">
          {trendsQuery.isPending ? <div className="chart-loading">正在聚合趋势数据…</div> : (
            <ResponsiveContainer width="100%" height={310}>
              <AreaChart data={trendsQuery.data?.items || []} margin={{ top: 14, right: 18, left: -14, bottom: 0 }}>
                <defs>
                  <linearGradient id="taskArea" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3977f6" stopOpacity={0.24} /><stop offset="95%" stopColor="#3977f6" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="#edf0f5" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8791a1" }} axisLine={false} tickLine={false} tickFormatter={(value: string) => value.slice(5)} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#8791a1" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e4e8ef", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10, paddingTop: 12 }} />
                <Area type="monotone" dataKey="tasks" name="创建任务" stroke="#3977f6" fill="url(#taskArea)" strokeWidth={2} />
                <Area type="monotone" dataKey="succeeded" name="成功" stroke="#22b95b" fill="transparent" strokeWidth={2} />
                <Area type="monotone" dataKey="failed" name="失败" stroke="#f25252" fill="transparent" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>
      <div className="report-insights">
        <article><CheckCircle size={24} weight="fill" /><span><strong>在线覆盖</strong><p>当前 {metrics?.onlineDevices || 0} 台设备在线，占设备总量 {metrics ? (metrics.onlineRate * 100).toFixed(1) : 0}% 。</p></span></article>
        <article className={metrics?.failedTasks ? "attention" : ""}>{metrics?.failedTasks ? <WarningCircle size={24} weight="fill" /> : <DeviceMobile size={24} />}<span><strong>任务质量</strong><p>{metrics?.failedTasks ? `窗口内有 ${metrics.failedTasks} 个失败或超时任务，建议进入任务管理查看设备级结果。` : "窗口内没有失败或超时任务。"}</p></span></article>
      </div>
      <section className="metric-definitions"><header><Info size={18} /><strong>指标口径</strong></header>{summaryQuery.data && Object.entries(summaryQuery.data.definitions).map(([name, definition]) => <div key={name}><code>{name}</code><p>{definition}</p></div>)}</section>
    </div>
  );
}
